import { type ReactNode, useEffect, useState } from "react";
import { Input } from "@tricura/atlas-ui";

interface DateTextFieldProps {
    label?: string;
    "aria-label"?: string;
    "data-autotest"?: string;
    isRequired?: boolean;
    isInvalid?: boolean;
    tooltip?: string;
    /** Helper text shown below the field (use for error messages). */
    hint?: ReactNode;
    size?: "sm" | "md" | "lg";
    defaultValue?: string;
    autoFocus?: boolean;
    inputClassName?: string;
    wrapperClassName?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
}

const format = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length === 0) return "";
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    return [mm, dd, yyyy].filter(Boolean).join("/");
};

export function DateTextField({
    label,
    "aria-label": ariaLabel,
    "data-autotest": dataAutotest,
    isRequired,
    isInvalid,
    tooltip,
    hint,
    size = "sm",
    defaultValue = "",
    autoFocus,
    inputClassName,
    wrapperClassName,
    onChange,
    onBlur,
}: DateTextFieldProps) {
    const [value, setValue] = useState(() => format(defaultValue));

    // Keep the input in sync when the form resets to a new defaultValue
    // (e.g. after a successful save round-trip or when switching submissions).
    useEffect(() => {
        setValue(format(defaultValue));
    }, [defaultValue]);

    const update = (raw: string) => {
        const formatted = format(raw);
        setValue(formatted);
        onChange?.(formatted);
    };

    return (
        <Input
            label={label}
            aria-label={ariaLabel}
            data-autotest={dataAutotest}
            isRequired={isRequired}
            isInvalid={isInvalid}
            tooltip={tooltip}
            hint={hint}
            size={size}
            placeholder="MM/DD/YYYY"
            value={value}
            onChange={update}
            onBlur={onBlur}
            maxLength={10}
            inputMode="numeric"
            autoComplete="off"
            autoFocus={autoFocus}
            inputClassName={inputClassName}
            wrapperClassName={wrapperClassName}
        />
    );
}
